import { useEffect, useState } from 'react';
import { TextInput } from '@woocommerce/blocks-components';
import { __ } from '@wordpress/i18n';
import styled from 'styled-components';
import { extensionCartUpdate } from '@woocommerce/blocks-checkout';
import Select from './Select';

const AchGroup = styled.div`
	display: flex;
	gap: 10px;
`;

export default function AchForm(props) {
    const {
        eventRegistration,
        emitResponse,
        paymentStatus,
    } = props;
    
    const { onPaymentSetup, onCheckoutAfterProcessingWithError } = eventRegistration;
    const [accountNumber, setAccountNumber] = useState('');
    const [accountNumberError, setAccountNumberError] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [routingNumberError, setRoutingNumberError] = useState('');
    const [nameOnAccount, setNameOnAccount] = useState('');
    const [nameOnAccountError, setNameOnAccountError] = useState('');
    const [accountType, setAccountType] = useState('C');
    const [entryClass, setEntryClass] = useState('Business');
    const [txnType, setTxnType] = useState('Debit');

    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneNumberError, setPhoneNumberError] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [contactValidationError, setContactValidationError] = useState('');
    

    useEffect(() => {
        const elements = document.querySelectorAll('.wc-block-components-payment-methods__save-card-info');
        // Filter elements to ensure they have the same parent
        const filteredElements = Array.from(elements).filter((element, index, array) => {
            return element.parentElement === array[0].parentElement; // Check if they have the same parent
        });
        if (filteredElements.length > 0) {
            filteredElements[0].style.display = 'none';
        }
        extensionCartUpdate( {
            namespace: 'wc-valorpay-fee-update',
            data: {
                action_type: 'update_payment_type',
                payment_type: "ach",
            },
        } );
        return () => {
            extensionCartUpdate( {
                namespace: 'wc-valorpay-fee-update',
                data: {
                    action_type: 'update_payment_type',
                    payment_type: "",
                },
            } )
            if (filteredElements.length > 0) {
                filteredElements[0].style.display = 'block';
            }
        };
    }, [])

    useEffect( () => {
        const unsubscribe = onPaymentSetup( async () => {
            let isInValidForm = false;
            if ( accountNumberError !== '' ) {
                isInValidForm = true;
            }
            if ( routingNumberError !== ''  ) {
                isInValidForm = true;
            }
            if ( nameOnAccountError !== ''  ) {
                isInValidForm = true;
            }
            if ( phoneNumberError !== ''  ) {
                isInValidForm = true;
            }
            if ( emailError !== ''  ) {
                isInValidForm = true;
            }
            if (accountNumber === '') {
                setAccountNumberError( __('Please enter a valid Account Number','wc-valorpay'));
                isInValidForm = true;
            }
            if (routingNumber === '') {
                setRoutingNumberError( __('Please enter a valid Routing Number','wc-valorpay'));
                isInValidForm = true;
            }
            if (nameOnAccount === '') {
                setNameOnAccountError( __('Please enter a valid Name On Account','wc-valorpay'));
                isInValidForm = true;
            }
            if (!validateContactInfo()) {
            isInValidForm = true;
            }
            if (isInValidForm) {
                return {
                    type: emitResponse.responseTypes.ERROR,
                    message: __(
                        'Please provide payment information',
                        'wc-valorpay'
                    ),
                };
            }
            return {
                type: emitResponse.responseTypes.SUCCESS,
                meta: {
                    paymentMethodData: {
                        wc_valorpay_ach_sale: true,
                        wc_valorpay_account_number: accountNumber,
                        wc_valorpay_routing_number: routingNumber,
                        wc_valorpay_name_on_account: nameOnAccount,
                        wc_valorpay_account_type: accountType,
                        wc_valorpay_entry_class: entryClass,
                        wc_valorpay_card_type: txnType,
                        wc_valorpay_phone: phoneNumber.replace(/[^0-9]/g, ''),
                        wc_valorpay_email: email,
                    },
                },
            };
        } );
        const unsubscribeErrorMsg = onCheckoutAfterProcessingWithError(
            ( { processingResponse } ) => {
                if ( processingResponse?.paymentDetails?.errorMessage ) {
                    return {
                        type: emitResponse.responseTypes.ERROR,
                        message: processingResponse.paymentDetails.errorMessage,
                        messageContext: emitResponse.noticeContexts.PAYMENTS,
                    };
                }
                // so we don't break the observers.
                return true;
            }
        );
        // Unsubscribes when this component is unmounted.
        return () => {
            unsubscribe();
            unsubscribeErrorMsg();
        };
    }, [
        emitResponse.responseTypes.ERROR,
        emitResponse.responseTypes.SUCCESS,
        onPaymentSetup,
        accountNumber,
        accountNumberError,
        routingNumber,
        routingNumberError,
        nameOnAccount,
        nameOnAccountError,
        accountType,
        entryClass,
        txnType,
        phoneNumber,
        phoneNumberError,
        email,
        emailError,
        contactValidationError,
    ] );

    const handleAccountNumberChange = (value) => {
        if ( value.length <= 13 && /^[0-9]*$/.test(value) ) {
            setAccountNumberError('');
            setAccountNumber(value);
        }
    };

    const handleAccountNumberBlur = (event) => {
        if ( accountNumber === '' ) {
            setAccountNumberError( __('Please enter a valid Account Number','wc-valorpay'));
        }
    }

    const handleRoutingNumberChange = (value) => {
        if (value.length <= 9 && /^[0-9]*$/.test(value)) {
            setRoutingNumberError('');
            setRoutingNumber(value);
        }
    };

    const handleRoutingNumberBlur = (event) => {
        if ( routingNumber === '' ) {
            setRoutingNumberError( __('Please enter a valid Routing Number','wc-valorpay'))
        }
    }

    const handleNameOnAccountChange = (value) => {
        if (value.length <= 22 && /^[a-zA-Z0-9\s]*$/.test(value)) {
            setNameOnAccountError('');
            setNameOnAccount(value);
        }
    };
    const handleNameOnAccountBlur = (value) => {
        if ( nameOnAccount === '' ) {
            setNameOnAccountError( __('Please enter a valid Name On Account','wc-valorpay'))
        }
    };

    const handlePhoneNumberChange = (value) => {
        // Remove any non-digit characters
        let numericValue = value.replace(/[^0-9]/g, '');

        // Format the number if it has 10 digits
        if (numericValue.length <= 10) {
            let formattedValue = numericValue;
            const previousVal = phoneNumber.replace(/[^0-9]/g, '');
            if (previousVal === numericValue) {
                numericValue = numericValue.slice(0, -1);
            }
            if (numericValue.length > 3 && numericValue.length <= 6) {
                formattedValue = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6)}`;
            } else if (numericValue.length > 6) {
                formattedValue = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
            } else if (numericValue.length > 0) {
                formattedValue = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
            }
        
            setPhoneNumber(formattedValue);
             if (numericValue.length > 0) {
            setPhoneNumberError('');
            setContactValidationError('');
        }
        
        // Validate contact info in real-time
        if (numericValue.length === 10 || email) {
            validateContactInfo();
        }
    }
    };
    
    const handlePhoneNumberBlur = () => {
    const formattedPhoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    
    // Only validate format if phone is provided
    if (formattedPhoneNumber && formattedPhoneNumber.length !== 10) {
        setPhoneNumberError(__('Please enter a valid phone number with 10 digits', 'wc-valorpay'));
    } else {
        setPhoneNumberError('');
    }
    
    // Validate the either/or condition
    validateContactInfo();
    };

    const handleEmailChange = (value) => {
        setEmail(value);
        
        // Clear errors when user starts typing
        if (value.length > 0) {
            setEmailError('');
            setContactValidationError('');
        }
        
        // Validate contact info in real-time when email looks complete
        if (value.includes('@') || phoneNumber.replace(/[^0-9]/g, '').length === 10) {
            validateContactInfo();
        }
    };

    const handleEmailBlur = () => {
        const emailRegex = /^[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i;
        
        // Only validate format if email is provided
        if (email && !emailRegex.test(email)) {
            setEmailError(__('Please enter a valid email address', 'wc-valorpay'));
        } else {
            setEmailError('');
        }
        
        // Validate the either/or condition
        validateContactInfo();
    };
        

    const handleAccountTypeChange = (event) => {
        setAccountType(event.target.value);
    };

    const handleEntryClassChange = (event) => {
        setEntryClass(event.target.value);
    };

    const handleTxnTypeChange = (event) => {
        setTxnType(event.target.value);
    };

    // Update the validation function
    const validateContactInfo = () => {
        const formattedPhone = phoneNumber.replace(/[^0-9]/g, '');
        const emailRegex = /^[a-z0-9._+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i;
        
        // Check if both are empty
        if (!formattedPhone && !email) {
            const errorMessage = __('Please provide either phone number or email', 'wc-valorpay');
            setContactValidationError(errorMessage);
            setPhoneNumberError(errorMessage);
            setEmailError(errorMessage);
            return false;
        }
        
        // Clear the either/or error if at least one is provided
        setContactValidationError('');
        
        // Check if phone is provided but invalid
        if (formattedPhone && formattedPhone.length !== 10) {
            setPhoneNumberError(__('Please enter a valid phone number with 10 digits', 'wc-valorpay'));
            return false;
        } else if (formattedPhone) {
            setPhoneNumberError('');
        }
        
        // Check if email is provided but invalid
        if (email && !emailRegex.test(email)) {
            setEmailError(__('Please enter a valid email address', 'wc-valorpay'));
            return false;
        } else if (email) {
            setEmailError('');
        }
        
        // Clear individual errors when both fields are valid
        if ((formattedPhone && formattedPhone.length === 10) || (email && emailRegex.test(email))) {
            setPhoneNumberError('');
            setEmailError('');
        }
        
        return true;
    };

    return (
    <>
        <TextInput
            id="valor-ach-acc-no"
            label={__('Account Number', 'wc-valorpay')}
            feedback={accountNumberError === '' ? '' : <div className="wc-block-components-validation-error"><p>{accountNumberError}</p></div>}
            type="text"
            value={accountNumber}
            required="true"
            className={accountNumberError === '' ? '' : "has-error"}
            onChange={handleAccountNumberChange}
            onBlur={handleAccountNumberBlur}
        />
        <TextInput
            id="valor-ach-rou-no"
            label={__('Routing Number', 'wc-valorpay')}
            feedback={routingNumberError === '' ? '' : <div className="wc-block-components-validation-error"><p>{routingNumberError}</p></div>}
            type="text"
            value={routingNumber}
            required="true"
            className={routingNumberError === '' ? '' : "has-error"}
            onChange={handleRoutingNumberChange}
            onBlur={handleRoutingNumberBlur}
        />
        <TextInput
            id="valor-ach-acc-name"
            label={__('Name on Account', 'wc-valorpay')}
            feedback={nameOnAccountError === '' ? '' : <div className="wc-block-components-validation-error"><p>{nameOnAccountError}</p></div>}
            type="text"
            value={nameOnAccount}
            required="true"
            className={nameOnAccountError === '' ? '' : "has-error"}
            onChange={handleNameOnAccountChange}
            onBlur={handleNameOnAccountBlur}
        />
        <Select
            id="valor-ach-acc-type"
            label="Account Type"
            options={[
            { label: "Checking", value: "C" },
            { label: "Savings", value: "S" },
            ]}
            value={accountType}
            selectOnChange={handleAccountTypeChange}
        />
        <AchGroup>
            <Select
                id="valor-ach-acc-entry"
                label="Entry Class"
                options={[
                { label: "Business", value: "Business" },
                { label: "Personal", value: "Personal" },
                ]}
                value={entryClass}
                selectOnChange={handleEntryClassChange}
            />
        </AchGroup>
        <TextInput
            id="valor-ach-phone"
            label={__('Phone Number', 'wc-valorpay')}
            feedback={phoneNumberError === '' ? '' : <div className="wc-block-components-validation-error"><p>{phoneNumberError}</p></div>}
            type="text"
            value={phoneNumber}
            className={phoneNumberError === '' ? '' : "has-error"}
            onChange={handlePhoneNumberChange}
            onBlur={handlePhoneNumberBlur}
        />
        <TextInput
            id="valor-ach-email"
            label={__('Email', 'wc-valorpay')}
            feedback={emailError === '' ? '' : <div className="wc-block-components-validation-error"><p>{emailError}</p></div>}
            type="email"
            value={email}
            className={emailError === '' ? '' : "has-error"}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
        />
    </>
)};